import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { DateTimePicker } from '@/components/ui/date-picker'
import { Label } from '@/components/ui/label'
import { GRACKLE_COLORS } from '@/lib/theme'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { useScheduleMeet } from '@/hooks/use-api'
import { Video, Calendar, Clock, Copy, Check, ExternalLink, X, AlertTriangle } from 'lucide-react'
import { explainError } from '@/lib/explain-error'

interface ScheduleMeetDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  entityType: 'contact' | 'account' | 'opportunity'
  entityId: string
  defaultTitle?: string
  defaultAttendees?: string[]
}

const DURATION_OPTIONS = [
  { value: '15', label: '15 minutes' },
  { value: '30', label: '30 minutes' },
  { value: '60', label: '1 hour' },
  { value: '90', label: '1.5 hours' },
  { value: '120', label: '2 hours' },
]

export function ScheduleMeetDialog({
  open,
  onOpenChange,
  entityType,
  entityId,
  defaultTitle = '',
  defaultAttendees = [],
}: ScheduleMeetDialogProps) {
  const { toast } = useToast()
  const scheduleMutation = useScheduleMeet()

  // Success state
  const [meetLink, setMeetLink] = useState<string | null>(null)
  const [googleEventLink, setGoogleEventLink] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  // Form state
  const [title, setTitle] = useState(defaultTitle)
  const [startDate, setStartDate] = useState('')
  const [duration, setDuration] = useState('30')
  const [attendees, setAttendees] = useState<string[]>(defaultAttendees)
  const [newAttendee, setNewAttendee] = useState('')
  const [description, setDescription] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [showConfirm, setShowConfirm] = useState(false)

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setTitle(defaultTitle)
      setAttendees(defaultAttendees)
      setMeetLink(null)
      setGoogleEventLink(null)
      setCopied(false)
      setDescription('')
      setErrors({})
      setShowConfirm(false)

      // Set default start time to next 30-minute slot
      const now = new Date()
      now.setMinutes(Math.ceil(now.getMinutes() / 30) * 30)
      now.setSeconds(0, 0)
      setStartDate(now.toISOString().slice(0, 16))
    }
  }, [open, defaultTitle, defaultAttendees])

  const calculateEndTime = (start: string, durationMinutes: number): string => {
    const startDate = new Date(start)
    const endDate = new Date(startDate.getTime() + durationMinutes * 60000)
    return endDate.toISOString()
  }

  const addAttendee = () => {
    const email = newAttendee.trim().toLowerCase()
    if (!email) return

    // Basic email validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErrors((prev) => ({ ...prev, attendee: 'Please enter a valid email address' }))
      return
    }

    if (attendees.includes(email)) {
      setErrors((prev) => ({ ...prev, attendee: 'This email is already added' }))
      return
    }

    setAttendees((prev) => [...prev, email])
    setNewAttendee('')
    setErrors((prev) => {
      const { attendee, ...rest } = prev
      return rest
    })
  }

  const removeAttendee = (email: string) => {
    setAttendees((prev) => prev.filter((a) => a !== email))
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      addAttendee()
    }
  }

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!title.trim()) {
      newErrors.title = 'Meeting title is required'
    }

    if (!startDate) {
      newErrors.startDate = 'Start date/time is required'
    } else {
      const start = new Date(startDate)
      if (start < new Date()) {
        newErrors.startDate = 'Start time must be in the future'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validate()) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields correctly.',
        variant: 'destructive',
      })
      return
    }

    try {
      const durationMinutes = parseInt(duration)
      const endTime = calculateEndTime(startDate, durationMinutes)

      const result = await scheduleMutation.mutateAsync({
        title,
        description,
        start_time: new Date(startDate).toISOString(),
        end_time: endTime,
        attendee_emails: attendees,
        entity_type: entityType,
        entity_id: entityId,
      })

      toast({
        title: 'Meeting Scheduled',
        description: `Google Meet created and calendar invites sent. Link: ${result.meet_link}`,
      })

      // Close the modal after successful creation
      onOpenChange(false)
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to schedule meeting')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  const copyMeetLink = async () => {
    if (!meetLink) return

    try {
      await navigator.clipboard.writeText(meetLink)
      setCopied(true)
      toast({
        title: 'Copied',
        description: 'Meet link copied to clipboard',
      })
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to copy link',
        variant: 'destructive',
      })
    }
  }

  const handleClose = () => {
    setMeetLink(null)
    setGoogleEventLink(null)
    onOpenChange(false)
  }

  // Success view
  if (meetLink) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-md" aria-describedby="meet-success-description">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Video className="h-5 w-5 text-[#2E7D5B]" aria-hidden="true" />
              Meeting Scheduled
            </DialogTitle>
            <DialogDescription id="meet-success-description">
              Your Google Meet has been created and calendar invites have been sent to attendees.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="text-sm text-muted-foreground">Meeting Title</Label>
              <p className="font-medium">{title}</p>
            </div>

            <div>
              <Label className="text-sm text-muted-foreground">Google Meet Link</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  value={meetLink}
                  readOnly
                  className="font-mono text-sm"
                  aria-label="Google Meet link"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={copyMeetLink}
                  aria-label={copied ? 'Copied' : 'Copy Meet link'}
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-[#2E7D5B]" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => window.open(meetLink, '_blank')}
              >
                <Video className="h-4 w-4 mr-2" aria-hidden="true" />
                Join Meeting
              </Button>
              {googleEventLink && (
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => window.open(googleEventLink, '_blank')}
                >
                  <ExternalLink className="h-4 w-4 mr-2" aria-hidden="true" />
                  View in Calendar
                </Button>
              )}
            </div>

            <div className="pt-4 border-t">
              <Button onClick={handleClose} className="w-full">
                Done
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  // Form view
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" aria-describedby="schedule-meet-description">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Video className="h-5 w-5" aria-hidden="true" />
            Schedule Google Meet
          </DialogTitle>
          <DialogDescription id="schedule-meet-description">
            Create a Google Calendar event with a Meet video conference link.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Title */}
          <div>
            <Label htmlFor="meet-title">Meeting Title *</Label>
            <Input
              id="meet-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter meeting title"
              aria-required="true"
              aria-invalid={!!errors.title}
              aria-describedby={errors.title ? 'title-error' : undefined}
            />
            {errors.title && (
              <p id="title-error" className="text-sm text-destructive mt-1" role="alert">
                {errors.title}
              </p>
            )}
          </div>

          {/* Date/Time and Duration */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="meet-start" className="flex items-center gap-1">
                <Calendar className="h-3 w-3" aria-hidden="true" />
                Start Date/Time *
              </Label>
              <DateTimePicker
                id="meet-start"
                value={startDate}
                onChange={(v) => setStartDate(v ?? '')}
                aria-label="Start Date/Time"
                aria-required="true"
                aria-invalid={!!errors.startDate}
                aria-describedby={errors.startDate ? 'start-error' : undefined}
              />
              {errors.startDate && (
                <p id="start-error" className="text-sm text-destructive mt-1" role="alert">
                  {errors.startDate}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="meet-duration" className="flex items-center gap-1">
                <Clock className="h-3 w-3" aria-hidden="true" />
                Duration
              </Label>
              <Select value={duration} onValueChange={setDuration}>
                <SelectTrigger id="meet-duration">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DURATION_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Attendees */}
          <div>
            <Label htmlFor="meet-attendee">Attendees</Label>
            <div className="flex gap-2">
              <Input
                id="meet-attendee"
                type="email"
                value={newAttendee}
                onChange={(e) => setNewAttendee(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter email and press Enter"
                aria-invalid={!!errors.attendee}
                aria-describedby={errors.attendee ? 'attendee-error' : 'attendee-hint'}
              />
              <Button type="button" variant="outline" onClick={addAttendee}>
                Add
              </Button>
            </div>
            {errors.attendee && (
              <p id="attendee-error" className="text-sm text-destructive mt-1" role="alert">
                {errors.attendee}
              </p>
            )}
            <p id="attendee-hint" className="text-xs text-muted-foreground mt-1">
              Attendees will receive calendar invites with the Meet link
            </p>

            {/* Attendee chips */}
            {attendees.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2" role="list" aria-label="Added attendees">
                {attendees.map((email) => (
                  <span
                    key={email}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-secondary text-secondary-foreground rounded-md text-sm"
                    role="listitem"
                  >
                    {email}
                    <button
                      type="button"
                      onClick={() => removeAttendee(email)}
                      className="hover:text-destructive"
                      aria-label={`Remove ${email}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Description */}
          <div>
            <Label htmlFor="meet-description">Description / Agenda</Label>
            <Textarea
              id="meet-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional meeting description or agenda"
              rows={3}
            />
          </div>

          {/* Warning Alert - shown when confirming */}
          {showConfirm && (
            <div
              className="flex items-center gap-3 p-4 rounded-md"
              style={{ backgroundColor: GRACKLE_COLORS.pollen, color: GRACKLE_COLORS.prussianBlue }}
              role="alert"
              aria-live="polite"
            >
              <AlertTriangle className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
              <p className="text-sm font-medium">
                Calendar invites will be automatically sent to all attendees listed above.
              </p>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowConfirm(false)
                onOpenChange(false)
              }}
              disabled={scheduleMutation.isPending}
            >
              Cancel
            </Button>
            {showConfirm ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowConfirm(false)}
                  disabled={scheduleMutation.isPending}
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  disabled={scheduleMutation.isPending}
                  style={{ backgroundColor: GRACKLE_COLORS.navy, color: '#ffffff' }}
                  className="hover:opacity-90"
                >
                  {scheduleMutation.isPending ? (
                    <>
                      <Video className="h-4 w-4 mr-2 animate-pulse" aria-hidden="true" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-2" aria-hidden="true" />
                      Confirm & Send Invites
                    </>
                  )}
                </Button>
              </>
            ) : (
              <Button
                type="button"
                onClick={() => {
                  if (validate()) {
                    setShowConfirm(true)
                  }
                }}
                style={{ backgroundColor: GRACKLE_COLORS.navy, color: '#ffffff' }}
                className="hover:opacity-90 font-semibold px-6"
              >
                <Video className="h-4 w-4 mr-2" aria-hidden="true" />
                Schedule Meeting
              </Button>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
